/**
 * Resolve entityRef for LiveContext state APIs.
 * entityRef format: entityType:entityId (e.g. Flow:flowId or Event:eventId).
 *
 * For LiveKit connection, use getOnAirLivekitRoomIdFromFlow instead.
 */

import type { LiveContextDto } from '@/types/dtos/live-contexts/live-context.dto';
import type { MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum';
import { resolveLiveBindingFlowId } from '@/helpers/object.id.helper';

export function getEntityRefFromFlow(flowId: string, liveContext?: LiveContextDto | null): string {
  if (liveContext?.entityType && liveContext?.entityId) {
    return `${liveContext.entityType}:${liveContext.entityId}`;
  }
  return `Flow:${flowId}`;
}

/**
 * Get the onAir LiveKit room ID for LiveKit connection (tokens, preview, etc.).
 * Returns entityType:entityId:onAir (e.g. Flow:flowId:onAir).
 * Returns undefined if no onAir room exists.
 */
export function getOnAirLivekitRoomIdFromFlow(
  _flowId: string,
  liveContext?: LiveContextDto | null,
): string | undefined {
  const onAir = liveContext?.livekitRooms?.find((r) => r.livekitRoomType === 'onAir');
  if (onAir?.livekitRoomId) {
    return onAir.livekitRoomId;
  }
  // Fallback: build from entityRef when liveContext is running (onAir room should exist)
  if (liveContext?.isRunning && liveContext?.entityType && liveContext?.entityId) {
    return `${liveContext.entityType}:${liveContext.entityId}:onAir`;
  }
  return undefined;
}

/** WebSocket subscription entity for player/live-context invalidation — must match server join-room { entityType, entityId }. */
export function resolveSocketEntityFromFlow(
  flow: {
    id: string;
    origin?: unknown;
    liveBindingFlowId?: string;
    liveContext?: LiveContextDto | null;
  },
  eventSlotId?: string,
): { entityType: MediaParentEntity; entityId: string } {
  // Prefer binding id always so start/stop invalidates are not missed during leave/rejoin
  // when isRunning flips (liveContext.entityId is the same binding id when present).
  const bindingEntityId =
    flow.liveContext?.entityType && flow.liveContext?.entityId
      ? flow.liveContext.entityId
      : resolveLiveBindingFlowId(flow, eventSlotId);
  return {
    entityType: (flow.liveContext?.entityType as MediaParentEntity | undefined) ?? 'Flow',
    entityId: bindingEntityId,
  };
}
